import { mobile, styled, Text, useTranslation } from '@obvio/app'
import { RichText } from '@obvio/template'
import { Tab } from '@obvio/ui'
import { slugify } from '@obvio/utils'

import { BlogIndexArticles } from './BlogIndexArticles'
import {
  CenteredSection,
  MaxWidthContent,
} from '../../Renderer/CenteredSection'
import { Heading } from '../../Renderer/RendererVariants/Heading'
import { HeroSection } from '../../Renderer/RendererVariants/HeroSection'
import { TabsMaxWidth } from '../../Renderer/RendererVariants/Tabs'
import { BlogPagination } from '../BlogPagination'

import type { DeepStringifyDate } from '@/typeUtils'
import type { Schema } from '@obvio/app'
import type { DeepPick } from '@obvio/utils'
import type { ReactElement } from 'react'

const StyledTabs = styled(TabsMaxWidth)<{ $count: number }>`
  .rc-tabs-tab {
    /* padding-bottom: 0 !important; */
    @media ${mobile} {
      min-width: 14rem;
    }
    .rc-tabs-tab-btn {
      width: 100%;
      text-align: center;
      > div {
        padding-bottom: ${(theme) => theme.spacing.small};
      }
    }
  }
  .rc-tabs-nav-list {
    display: grid !important;
    grid-template-columns: ${(_, { $count }) => `repeat(${$count}, 1fr)`};
    @media ${mobile} {
      width: unset !important;
    }
  }
`

export const ArticleSelect = {
  id: true,
  createdAt: true,
  title: true,
  excerpt: true,
  slug: true,
  image: true,
  category: {
    title: true,
  },
  visible: true,
} as const

export const ArticleCategorySelect = {
  id: true,
  title: true,
  articles: ArticleSelect,
} as const

export type ArticleCategory = DeepStringifyDate<
  DeepPick<Schema['ArticleCategory'], typeof ArticleCategorySelect>
>

type BlogIndexProps = {
  categories: ArticleCategory[]
  /** Number of archive pages linked below the tabs. */
  pages: number
}

function getLatestArticles(
  categories: BlogIndexProps['categories'],
  count = 6,
) {
  const articles = categories
    .reduce<ArticleCategory['articles']>((acc, category) => {
      return [...acc, ...category.articles]
    }, [])
    .sort((a, b) => (new Date(a.createdAt) > new Date(b.createdAt) ? -1 : 1))

  return articles.slice(0, count)
}
export function BlogIndex({ categories, pages }: BlogIndexProps): ReactElement {
  const allLatest = getLatestArticles(categories)
  const { t } = useTranslation()

  const categoriesWithAll: ArticleCategory[] = [
    {
      id: 'all',
      title: t('label.all'),
      articles: allLatest,
    },
    ...categories,
  ]

  const categoriesCount = categoriesWithAll.length
  return (
    <>
      <HeroSection heading img="/static/img/blog_tło.jpeg" text="BLOG" />
      <Heading heading="DZIENNIK HOTELOWY" description="#*NASZE* SPOJRZENIE" />
      <CenteredSection>
        <MaxWidthContent>
          <RichText value="511 m n.p.m w sercu Jury Krakowsko-Częstochowskiej, wśród wapiennych skał. W bliskim otoczeniu zabytków architektonicznych z minionych wieków i w bezpośrednim kontakcie z naturą w jej najczystszej, surowej formie. Witaj w Poziom 511." />
        </MaxWidthContent>
      </CenteredSection>
      <CenteredSection>
        <StyledTabs keepInactiveTabPane $count={categoriesCount}>
          {categoriesWithAll.map((category) => {
            const key = slugify(category.title)
            return (
              <Tab key={key} label={category.title}>
                <BlogIndexArticles articles={category.articles} />
              </Tab>
            )
          })}
        </StyledTabs>
      </CenteredSection>
      {pages > 1 ? (
        <CenteredSection>
          <MaxWidthContent>
            <Text tag="h2">{t('blog.allPosts')}</Text>
            <BlogPagination total={pages} />
          </MaxWidthContent>
        </CenteredSection>
      ) : null}
    </>
  )
}
